import { FormEvent, useEffect, useState } from 'react'
import useHelper from '../../../../../hooks/useHelper'
import { post } from '../../../../../utilities/http'
import { Exception } from '../../../../../utilities/http/type'
import Notify from '../../../../../utilities/Notify'
import val from '../../../../../utilities/val'
import { normalize } from '../../../../../utilities/valueTools'
import { providerList } from '../../../storage'
import preflight from '../utilities/preflight'
import { HelperData, RowError, SubmitValues, UploadState } from './types'

interface ValidateResult {
    errors: RowError[]
    success: boolean
}

interface ExcelRow {
    product_code: string | null
    provider_code: string | null
    purchase_price: number | string | null
    provider_description: string | null
}

const COL_PRODUCT_CODE = 0
const COL_PROVIDER_CODE = 1
const COL_PURCHASE_PRICE = 3
const COL_PROVIDER_DESCRIPTION = 4

const ROW_FIELD_PATTERN = /^product_provider_companies\.(\d+)\.(.+)$/

function hasErrors(findings: RowError[]): boolean {
    return findings.some(finding => finding.severity !== 'warning')
}

function text(value: unknown): string | null {
    return value ? normalize(value) : null
}

function parsePostErrors(error: Exception): RowError[] {
    const stacks = Object.values(error?.errors || {})
    return stacks.map(stack => {
        const match = ROW_FIELD_PATTERN.exec(stack.field_error || '')
        return {
            index: match ? Number(match[1]) + 1 : null,
            field: match ? match[2] : stack.field_error,
            message: stack.description,
            severity: 'error' as const
        }
    })
}

export default function useSubmit(
    values: SubmitValues,
    close: () => void,
    onDone: () => void
) {
    const { helper, setHelper } = useHelper<HelperData>()
    const [uploadStates, setUploadStates] = useState<UploadState[]>([])
    const [rowsError, setRowsError] = useState<RowError[]>([])
    const [pendingConfirmation, setPendingConfirmation] = useState(false)

    useEffect(() => {
        setHelper({
            ...helper,
            company: ''
        })
    }, [values.optionCompany])
    useEffect(() => {
        setHelper({
            ...helper,
            money: ''
        })
    }, [values.optionMoney])
    useEffect(() => {
        setHelper({
            ...helper,
            importExcel: ''
        })
    }, [values.excelRendered])

    useEffect(() => {
        setPendingConfirmation(false)
        setRowsError([])
        setUploadStates([])
    }, [values.optionCompany, values.optionMoney, values.excelRendered])

    function updateState(
        position: number,
        status: UploadState['status'],
        message: string
    ) {
        setUploadStates(prevStates =>
            prevStates.map((state, i) =>
                i === position ? { status, message } : state
            )
        )
    }

    function buildRows(): ExcelRow[] {
        return values.excelRendered.rows
            .filter(row => row.length > 0)
            .slice(1)
            .map(row => {
                const price = row[COL_PURCHASE_PRICE]
                return {
                    product_code: text(row[COL_PRODUCT_CODE]),
                    provider_code: text(row[COL_PROVIDER_CODE]),
                    purchase_price: isNaN(price as number)
                        ? price || null
                        : Number(price),
                    provider_description: text(row[COL_PROVIDER_DESCRIPTION])
                }
            })
    }

    async function submit(ev: FormEvent<HTMLFormElement>) {
        ev.preventDefault()
        setUploadStates([])
        setHelper({})
        setRowsError([])

        const v = val(values)
        v.optionCompany.isEmpty('Es requerido la compañía')
        v.optionMoney.isEmpty('Es requerido la moneda')
        v.excelRendered.isEmpty('Es requerido el excel')

        const result = v.runtest()
        if (result) {
            setHelper({
                company: result.optionCompany,
                money: result.optionMoney,
                importExcel: result.excelRendered
            })
            return
        }

        const localFindings = preflight(
            values.excelRendered,
            values.optionMoney,
            values.currencies
        )

        if (hasErrors(localFindings)) {
            setUploadStates([
                {
                    status: 'error',
                    message: 'Validando el formato del archivo'
                }
            ])
            setRowsError(localFindings)
            return
        }

        setUploadStates([
            {
                status: 'done',
                message: 'Formato del archivo validado'
            },
            {
                status: 'loading',
                message: 'Validando productos'
            }
        ])

        const rowsOnlyData = buildRows()
        const providerId = providerList.filter.params.provider_id
        const { error, data } = await post<ValidateResult>(
            '/v5.6/apis/product_provider_companies/validate',
            {
                company_id: parseInt(values.optionCompany.value),
                currency_id: parseInt(values.optionMoney.value),
                product_provider_companies: rowsOnlyData,
                provider_id: parseInt(providerId)
            }
        )

        const remoteFindings = data?.result?.errors || []
        const findings = [...localFindings, ...remoteFindings]

        if (error || hasErrors(findings)) {
            updateState(1, 'error', 'Validando productos')
            setRowsError(
                error ? [...findings, ...parsePostErrors(error)] : findings
            )
            if (error) Notify.error('Lo sentimos hubo un error')
            return
        }

        if (findings.length > 0 && !pendingConfirmation) {
            updateState(1, 'warning', 'Validando productos')
            setRowsError(findings)
            setPendingConfirmation(true)
            return
        }

        updateState(1, 'done', 'Validando productos')
        setUploadStates(prevStates => [
            ...prevStates,
            {
                status: 'loading' as UploadState['status'],
                message: 'Importando tus productos'
            }
        ])
        setRowsError(findings)

        const { error: err } = await post(
            '/v5.6/apis/product_provider_companies',
            {
                product_provider_companies: rowsOnlyData.map(row => ({
                    company_id: parseInt(values.optionCompany.value),
                    currency_id: parseInt(values.optionMoney.value),
                    product_code: row.product_code,
                    provider_code: row.provider_code,
                    purchase_price: row.purchase_price,
                    provider_description: row.provider_description
                })),
                provider_id: parseInt(providerId)
            }
        )

        if (err) {
            updateState(2, 'error', 'Importando tus productos')
            setRowsError([...findings, ...parsePostErrors(err)])
            Notify.error('Lo sentimos hubo un error')
            return
        }

        Notify.success('La operación se ha completado exitosamente')
        setUploadStates([])
        setPendingConfirmation(false)
        close()
        onDone()
    }

    return {
        submit,
        helper,
        setHelper,
        uploadStates,
        rowsError,
        setRowsError,
        setUploadStates,
        pendingConfirmation,
        setPendingConfirmation
    }
}
